import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { initTracking, refreshConfig, trackPageView, trackUrlSent, trackEmailSent } from "./tracking";

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function animateScan(setScanX, duration) {
  return new Promise(resolve => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      setScanX(eased * 110 - 5);
      if (p < 1) requestAnimationFrame(tick);
      else resolve();
    };
    requestAnimationFrame(tick);
  });
}

function SkeletonLine({ width = "100%", height = 8, mb = 0 }) {
  return (
    <div style={{
      width, height, borderRadius: 5, marginBottom: mb,
      background: "linear-gradient(90deg,#e8eaed 25%,#f3f4f6 50%,#e8eaed 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmerMF 1.8s infinite",
    }}/>
  );
}

// A single Google Shopping product card
function ProductCard({ isOur, title, price, optimized, ranked, scanX, scanning, dim }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      border: isOur && optimized ? "2px solid #4B6AE8" : "1.5px solid #e8eaed",
      boxShadow: isOur && optimized
        ? "0 8px 32px rgba(75,106,232,0.22)"
        : "0 1px 4px rgba(0,0,0,0.06)",
      overflow: "hidden",
      position: "relative",
      flex: "1 1 0",
      minWidth: 0,
      height: 240,
      opacity: dim ? 0.38 : 1,
      transform: "scale(1)",
      transition: "opacity 0.5s ease, transform 0.6s cubic-bezier(0.34,1.2,0.64,1), border 0.4s ease, box-shadow 0.4s ease",
      zIndex: isOur ? 2 : 1,
    }}>

      {/* Scan beam — only on our card */}
      {scanning && isOur && (
        <>
          <div style={{
            position: "absolute", inset: 0, zIndex: 9, pointerEvents: "none",
            background: `linear-gradient(90deg, rgba(75,106,232,0.07) ${scanX - 20}%, rgba(75,106,232,0.0) ${scanX + 20}%)`,
          }}/>
          <div style={{
            position: "absolute", top: 0, bottom: 0, left: `${scanX}%`,
            width: 3, zIndex: 10, pointerEvents: "none",
            background: "linear-gradient(180deg, transparent 0%, #818cf8 20%, #4B6AE8 50%, #818cf8 80%, transparent 100%)",
            boxShadow: "0 0 18px 7px rgba(75,106,232,0.5)",
          }}/>
        </>
      )}

      {/* Image zone */}
      <div style={{
        height: 90,
        background: isOur
          ? (optimized ? "linear-gradient(135deg,#eef2ff,#dde5ff)" : "#f5f5f5")
          : "#f5f5f5",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.6s ease",
        position: "relative",
      }}>
        {isOur ? (
          <img
            src="/backpack.png"
            alt="Laptop backpack"
            style={{ width: "80%", height: "85%", objectFit: "contain" }}
          />
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="3" stroke="#d1d5db" strokeWidth="1.5"/>
            <path d="M3 16l5-5 4 4 3-3 6 6" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="8.5" r="1.5" fill="#d1d5db"/>
          </svg>
        )}

        {/* #1 badge on our card when ranked #1 */}
        {isOur && ranked && (
          <div style={{
            position: "absolute", top: 8, left: 8,
            background: "linear-gradient(135deg,#4B6AE8,#818cf8)",
            color: "#fff", fontSize: 11, fontWeight: 800,
            padding: "3px 9px", borderRadius: 20,
            boxShadow: "0 2px 8px rgba(75,106,232,0.45)",
            animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)",
          }}>#1</div>
        )}
      </div>

      {/* Text content */}
      <div style={{ padding: "8px 10px 10px" }}>
        {/* AI badge */}
        <div style={{
          height: 22, marginBottom: 6,
          opacity: isOur && optimized ? 1 : 0,
          transform: isOur && optimized ? "translateY(0)" : "translateY(4px)",
          transition: "all 0.4s ease 0.2s",
        }}>
          <span style={{
            fontSize: 8, fontWeight: 600, color: "#4B6AE8",
            background: "rgba(75,106,232,0.08)",
            border: "1px solid rgba(75,106,232,0.18)",
            padding: "2px 6px", borderRadius: 20,
            whiteSpace: "nowrap",
          }}>✦ AI Optimized</span>
        </div>

        {/* Title */}
        <div style={{ minHeight: 36, marginBottom: 4 }}>
          {isOur ? (
            <div style={{ fontSize: 12, fontWeight: 600, color: "#111", lineHeight: 1.4 }}>
              {title}
              {/* blinking cursor while rewriting */}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: "#9aa0a6", marginBottom: 5 }}>Product</div>
              <SkeletonLine width="85%" height={7} mb={4}/>
              <SkeletonLine width="60%" height={7}/>
            </>
          )}
        </div>

        {isOur ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 8 }}>{price}</div>
        ) : (
          <div style={{ marginBottom: 8 }}><SkeletonLine width="45%" height={8}/></div>
        )}

        <SkeletonLine width="90%" height={6} mb={4}/>
        <SkeletonLine width="70%" height={6}/>
      </div>
    </div>
  );
}

function ShoppingWidget() {
  // phases: before → scanning → rewriting → ranked
  const [phase, setPhase] = useState("before");
  const [scanX, setScanX] = useState(-10);
  const [title, setTitle] = useState("Laptop backpack");
  const ivRef = useRef(null);
  const running = useRef(true);

  const BEFORE_TITLE = "Laptop backpack";
  const AFTER_TITLE = 'Premium & Durable Luxury Laptop Backpack — 15.6"';

  const isOptimized = phase === "rewriting" || phase === "ranked";
  const isScanning = phase === "scanning";
  const isRanked = phase === "ranked";

  // The two visible cards.
  // Before: [ghost #1, our #2]
  // After ranked: [our #1, ghost #2]
  // We animate with CSS translateX on a flex row.

  useEffect(() => {
    running.current = true;
    const run = async () => {
      while (running.current) {
        // RESET
        setPhase("before");
        setTitle(BEFORE_TITLE);
        setScanX(-10);
        await sleep(2500);
        if (!running.current) break;

        // SCAN
        setPhase("scanning");
        await animateScan(setScanX, 1400);
        if (!running.current) break;

        // REWRITE (typewriter)
        setPhase("rewriting");
        clearInterval(ivRef.current);
        setTitle("");
        let i = 0;
        await new Promise(resolve => {
          ivRef.current = setInterval(() => {
            i++;
            setTitle(AFTER_TITLE.slice(0, i));
            if (i >= AFTER_TITLE.length) { clearInterval(ivRef.current); resolve(); }
          }, 22);
        });
        if (!running.current) break;
        await sleep(600);

        // RANKED — slide to #1
        setPhase("ranked");
        await sleep(4200);
        if (!running.current) break;
      }
    };
    run();
    return () => { running.current = false; clearInterval(ivRef.current); };
  }, []);

  return (
    <div style={{
      background: "#fff", borderRadius: 14,
      border: "1px solid rgba(75,106,232,0.1)",
      boxShadow: "0 4px 20px rgba(75,106,232,0.07)",
      overflow: "hidden",
    }}>
      {/* Fake Google Shopping search bar */}
      <div style={{ padding: "10px 12px 0" }}>
        {/* Google Shopping logo */}
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.77-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.56l7.97-5.97z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.97C6.51 42.62 14.62 48 24 48z"/></svg>
          <span style={{ fontSize:13, fontWeight:500, color:"#5f6368" }}>Shopping</span>
        </div>
        <div style={{
          display: "flex", alignItems: "center",
          background: "#f8f9fa", border: "1px solid #dadce0",
          borderRadius: 24, padding: "6px 14px", marginBottom: 8, gap: 8,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <span style={{ fontSize: 13, color: "#202124", flex: 1 }}>Luxury Laptop Backpack</span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 20 }}>
          {["All","Shopping","Images","News"].map((t,i) => (
            <div key={t} style={{
              fontSize: 11, paddingBottom: 7,
              fontWeight: i === 1 ? 600 : 400,
              color: i === 1 ? "#4B6AE8" : "#5f6368",
              borderBottom: i === 1 ? "2.5px solid #4B6AE8" : "2.5px solid transparent",
            }}>{t}</div>
          ))}
        </div>
      </div>

      {/* "Sponsored products" row */}
      <div style={{ padding: "8px 12px 6px", display: "flex", alignItems: "center", gap: 6, height: 36 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#202124" }}>Sponsored products</span>
        {isRanked && (
          <div style={{
            marginLeft: "auto", display: "flex", alignItems: "center", gap: 6,
            background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
            border: "1px solid #6ee7b7",
            borderRadius: 20, padding: "4px 12px",
            animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1)",
            boxShadow: "0 2px 8px rgba(16,185,129,0.2)",
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", animation: "pulseMF 1.8s infinite" }}/>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#059669" }}>Your product is now #1 🎉</span>
          </div>
        )}
      </div>

      {/* Cards row */}
      <div style={{ padding: "6px 14px 12px", overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 10 }}>
          {/* Ghost card - slot 0, swaps right when ranked */}
          <div style={{
            flex: "1 1 0", minWidth: 0,
            transform: isRanked ? "translateX(calc(100% + 10px))" : "translateX(0)",
            transition: "transform 0.75s cubic-bezier(0.34,1.2,0.64,1)",
          }}>
            <ProductCard isOur={false} dim={isRanked} />
          </div>
          {/* Our card - slot 1, swaps left to #1 when ranked */}
          <div style={{
            flex: "1 1 0", minWidth: 0,
            transform: isRanked ? "translateX(calc(-100% - 10px))" : "translateX(0)",
            transition: "transform 0.75s cubic-bezier(0.34,1.2,0.64,1)",
          }}>
            <ProductCard
              isOur={true}
              title={title}
              price="$89.99"
              optimized={isOptimized}
              ranked={isRanked}
              scanX={scanX}
              scanning={isScanning}
              dim={false}
            />
          </div>
          {/* Static ghost cards */}
          <ProductCard isOur={false} dim={false} />
          <div className="mf-hide-mobile" style={{ flex:"1 1 0", minWidth:0 }}><ProductCard isOur={false} dim={false} /></div>
        </div>
      </div>
    </div>
  );
}

const SCAN_PRODUCTS = [
  {
    title: "Black T-Shirt", desc: "Comfortable cotton t-shirt.",
    img: "/products/tshirt.avif",
    titleScore: 18, titleLabel: "Too short, missing brand & attributes",
    descScore: 22, descLabel: "Too short (under 500 chars)",
    hlLabel: "Not configured", hlColor: "#EF4444", hlBg: "#FEE2E2",
    overall: 21, scoreColor: "#EF4444", borderColor: "rgba(239,68,68,0.25)", shadowColor: "rgba(239,68,68,0.08)",
  },
  {
    title: "Running Shoes", desc: "Great for running.",
    img: "/products/shoes.webp",
    titleScore: 24, titleLabel: "Missing brand, color & material",
    descScore: 31, descLabel: "Not keyword-rich enough",
    hlLabel: "Not configured", hlColor: "#EF4444", hlBg: "#FEE2E2",
    overall: 26, scoreColor: "#EF4444", borderColor: "rgba(239,68,68,0.25)", shadowColor: "rgba(239,68,68,0.08)",
  },
  {
    title: "Leather Wallet", desc: "Brown leather wallet for men.",
    img: "/products/wallet.jpg",
    titleScore: 61, titleLabel: "Good length but missing brand",
    descScore: 58, descLabel: "Decent but could be more specific",
    hlLabel: "2/6 only", hlColor: "#F59E0B", hlBg: "#FEF3C7",
    overall: 58, scoreColor: "#F59E0B", borderColor: "rgba(245,158,11,0.25)", shadowColor: "rgba(245,158,11,0.08)",
  },
];

function ScanProductCard({ product, scanning, showScore, visibleRows, rowScores }) {
  const glowing = scanning;
  const hasRows = visibleRows > 0;

  const rows = [
    { icon: "\ud83d\udcdd", label: "Title quality", targetScore: product.titleScore, reason: product.titleLabel, color: product.titleScore < 40 ? "#EF4444" : "#F59E0B" },
    { icon: "\ud83d\udcc4", label: "Description score", targetScore: product.descScore, reason: product.descLabel, color: product.descScore < 40 ? "#EF4444" : "#F59E0B" },
    { icon: "\u2b50", label: "Highlights", targetScore: null, reason: product.hlLabel, color: product.hlColor },
  ];

  return (
    <div style={{
      flex: "1 1 0", minWidth: 0, background: "#fff", borderRadius: 8,
      border: glowing ? "1.5px solid rgba(75,106,232,0.5)" : hasRows ? `1.5px solid ${product.borderColor}` : "1px solid #e1e3e5",
      overflow: "hidden", position: "relative",
      transition: "border 0.3s ease, box-shadow 0.3s ease",
      boxShadow: glowing ? "0 0 16px rgba(75,106,232,0.2)" : hasRows ? `0 2px 12px ${product.shadowColor}` : "0 1px 2px rgba(0,0,0,0.05)",
    }}>
      {/* Scan line */}
      {scanning && (
        <div style={{
          position: "absolute", left: 0, right: 0, height: 2, zIndex: 10,
          background: "linear-gradient(90deg, transparent 0%, #4B6AE8 30%, #4B6AE8 70%, transparent 100%)",
          opacity: 0.6,
          animation: "scanDown 2.5s ease-in-out forwards",
        }}/>
      )}

      {/* Score pill — top right */}
      <div style={{
        position: "absolute", top: 6, right: 6, borderRadius: 20,
        background: product.scoreColor, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "4px 10px",
        opacity: showScore ? 1 : 0, transform: showScore ? "scale(1)" : "scale(0.5)",
        transition: "opacity 0.3s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        zIndex: 5,
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>Score {product.overall}/100</span>
      </div>

      {/* Product image — square 1:1 on white */}
      <div style={{
        width: "100%", aspectRatio: "1/1", background: "#fff",
        borderBottom: "1px solid #f1f1f1",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 12,
      }}>
        <img src={product.img} alt={product.title} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 4 }}/>
      </div>

      {/* Product name + score table */}
      <div style={{ padding: "8px 10px 10px" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 6, lineHeight: 1.3 }}>
          {product.title}
        </div>

        {/* Score table rows */}
        <div style={{ overflow: "hidden" }}>
          {rows.map((row, i) => {
            const visible = visibleRows > i;
            const currentScore = rowScores[i];
            const isHighlights = i === 2;
            const done = isHighlights ? currentScore > 0 : currentScore >= row.targetScore;

            let scoreText, reasonText;
            if (!visible) {
              scoreText = ""; reasonText = "";
            } else if (isHighlights) {
              scoreText = currentScore === -1 ? "checking..." : (product.hlLabel === "Not configured" ? "✗ Not configured" : product.hlLabel);
              reasonText = "";
            } else {
              scoreText = `${currentScore}/100`;
              reasonText = done ? row.reason : "";
            }
            const scoreColor = isHighlights
              ? (currentScore === -1 ? "#94a3b8" : row.color)
              : (currentScore > 0 ? row.color : "#94a3b8");

            return (
              <div key={i} style={{
                padding: visible ? "6px 0" : "0",
                opacity: visible ? 1 : 0,
                borderTop: i > 0 && visible ? "1px solid #f3f4f6" : "none",
                overflow: "hidden",
                maxHeight: visible ? 50 : 0,
                transition: "max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease",
              }}>
                {/* Line 1: icon + label + score */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 10, flexShrink: 0 }}>{row.icon}</span>
                  <span style={{ fontSize: 10, color: "#6b7280", flexShrink: 0 }}>{row.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: scoreColor, marginLeft: "auto", flexShrink: 0 }}>{scoreText}</span>
                </div>
                {/* Line 2: reason */}
                {reasonText && (
                  <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2, paddingLeft: 18, lineHeight: 1.3 }}>
                    {reasonText}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AuditPreviewCard() {
  // Static — all scores already revealed
  const cardStates = SCAN_PRODUCTS.map(p => ({
    visibleRows: 3,
    showScore: true,
    rowScores: [p.titleScore, p.descScore, 1],
  }));

  return (
    <div className="mf-audit-card" style={{
      background: "#fff", borderRadius: 14,
      border: "1px solid rgba(75,106,232,0.1)",
      boxShadow: "0 4px 20px rgba(75,106,232,0.07)",
      padding: "16px 18px", marginTop: 20, marginBottom: 20,
    }}>
      <style>{`
        @keyframes scanDown {
          0% { top: 0; }
          100% { top: 100%; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981" }}/>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>AI analysis complete — 3 products scanned</span>
      </div>

      {/* Product cards row */}
      <div className="mf-product-cards" style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        {SCAN_PRODUCTS.map((product, i) => (
          <ScanProductCard
            key={i}
            product={product}
            scanning={false}
            showScore={cardStates[i].showScore}
            visibleRows={cardStates[i].visibleRows}
            rowScores={cardStates[i].rowScores}
          />
        ))}
      </div>

      {/* Summary card */}
      <div style={{
        background: "#fff", borderRadius: 8, border: "1px solid #e1e3e5",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}>
        <div className="mf-summary-inner" style={{ display: "flex", padding: 16 }}>
          {/* Left column — score */}
          <div className="mf-summary-left" style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingRight: 20 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
              <span className="mf-score-big" style={{ fontSize: 48, fontWeight: 800, color: "#EF4444", lineHeight: 1, letterSpacing: -2 }}>32</span>
              <span style={{ fontSize: 18, fontWeight: 500, color: "#94a3b8" }}>/100</span>
            </div>
            <div className="mf-score-label" style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>Feed Quality Score</div>
            <div style={{ marginTop: 6, background: "#FEE2E2", borderRadius: 20, padding: "3px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 8, lineHeight: 1 }}>🔴</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#EF4444" }}>Critical issues detected</span>
            </div>
          </div>

          {/* Divider */}
          <div className="mf-summary-divider" style={{ width: 1, background: "#E5E7EB", alignSelf: "stretch", flexShrink: 0 }}/>

          {/* Right column — issues */}
          <div className="mf-summary-right" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingLeft: 20, gap: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <span style={{ fontSize: 13, color: "#EF4444", flexShrink: 0, lineHeight: 1.4 }}>⚠</span>
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.4 }}>Titles too short on <strong style={{ color: "#0f172a" }}>2/3 products</strong></span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <span style={{ fontSize: 13, color: "#EF4444", flexShrink: 0, lineHeight: 1.4 }}>⚠</span>
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.4 }}>Product Highlights missing on <strong style={{ color: "#0f172a" }}>87% of products</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const FIX_ISSUES = [
  { text: "Titles too short on 2/3 products", severity: "Critical" },
  { text: "Brand name missing in all titles", severity: "Critical" },
  { text: "Product Highlights not configured", severity: "Critical" },
  { text: "Descriptions under 500 chars", severity: "Warning" },
  { text: "No material or color attributes", severity: "Warning" },
];
const FIX_SCORES = [32, 45, 61, 78, 94, 98];

function getScoreColor(s) {
  if (s < 40) return "#EF4444";
  if (s < 70) return "#F59E0B";
  return "#10B981";
}

function AIFixCard({ started }) {
  const [fixedRows, setFixedRows] = useState(0);
  const [score, setScore] = useState(32);
  const [showCTA, setShowCTA] = useState(false);
  const [optimizedLabel, setOptimizedLabel] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [buttonActive, setButtonActive] = useState(false);
  const running = useRef(true);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!started || hasRun.current) return;
    hasRun.current = true;
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });

    const countTo = (from, to, duration) => {
      return new Promise(resolve => {
        const start = Date.now();
        const tick = () => {
          if (!running.current) { resolve(); return; }
          const p = Math.min((Date.now() - start) / duration, 1);
          setScore(Math.round(from + (to - from) * p));
          if (p < 1) requestAnimationFrame(tick);
          else resolve();
        };
        requestAnimationFrame(tick);
      });
    };

    const runOnce = async () => {
      await wait(800);
      if (!running.current) return;

      // Button glows
      setButtonActive(true);
      await wait(800);
      if (!running.current) return;

      // Fix rows one by one + increment score
      for (let i = 0; i < 5; i++) {
        setFixedRows(i + 1);
        await countTo(FIX_SCORES[i], FIX_SCORES[i + 1], 300);
        if (!running.current) return;
        await wait(300);
        if (!running.current) return;
      }

      // Optimized label + confetti
      setOptimizedLabel(true);
      setConfetti(true);
      await wait(1500);
      if (!running.current) return;
      setConfetti(false);

      // Show CTA
      setShowCTA(true);
    };

    runOnce();
    return () => { running.current = false; clearTimeout(timeout); };
  }, [started]);

  const scoreColor = getScoreColor(score);

  return (
    <div style={{ marginTop: 0, marginBottom: 20 }}>
      {/* Main fix card */}
      <div style={{
        background: "#fff", borderRadius: 8, border: "1px solid #e1e3e5",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)", overflow: "hidden",
      }}>
        <div style={{ display: "flex", minHeight: 220 }}>

          {/* Left column — issues */}
          <div style={{ flex: 1, padding: "18px 20px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", marginBottom: 12 }}>Issues detected</div>
            {FIX_ISSUES.map((issue, i) => {
              const fixed = i < fixedRows;
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", marginBottom: 4,
                  borderRadius: 8,
                  background: fixed ? "#F0FDF4" : "transparent",
                  transition: "background 0.3s ease",
                }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, flexShrink: 0,
                    color: fixed ? "#10B981" : "#EF4444",
                    transition: "color 0.3s ease",
                  }}>
                    {fixed ? "✓" : "✗"}
                  </span>
                  <span style={{
                    fontSize: 12, flex: 1,
                    color: fixed ? "#059669" : "#6b7280",
                    transition: "color 0.3s ease",
                  }}>
                    {issue.text}
                  </span>
                  {!fixed && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                      color: issue.severity === "Critical" ? "#EF4444" : "#F59E0B",
                      background: issue.severity === "Critical" ? "#FEE2E2" : "#FEF3C7",
                      flexShrink: 0,
                    }}>
                      {issue.severity}
                    </span>
                  )}
                </div>
              );
            })}

            {/* Fix button */}
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <button style={{
                background: buttonActive ? "linear-gradient(135deg,#4B6AE8,#6B8BF5)" : "linear-gradient(135deg,#94a3b8,#b0bec5)",
                border: "none", borderRadius: 10, padding: "10px 24px",
                color: "#fff", fontSize: 13, fontWeight: 600, cursor: "default",
                fontFamily: "'Inter',sans-serif",
                boxShadow: buttonActive ? "0 4px 16px rgba(75,106,232,0.35)" : "none",
                transform: buttonActive && fixedRows === 0 ? "scale(1.03)" : "scale(1)",
                transition: "all 0.3s ease",
              }}>
                ✦ Apply AI fixes
              </button>
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: 1, background: "#E5E7EB", flexShrink: 0 }}/>

          {/* Right column — score */}
          <div style={{ width: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20, position: "relative", overflow: "hidden" }}>
            {/* Confetti */}
            {confetti && (
              <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 10 }}>
                {[[30,80,"#10B981",0],[55,75,"#4B6AE8",0.1],[70,85,"#F59E0B",0.2],[45,70,"#EF4444",0.15],[60,90,"#818cf8",0.05],[35,65,"#34d399",0.12]].map(([x,startY,color,delay],i) => (
                  <div key={i} style={{
                    position: "absolute", left: `${x}%`, bottom: `${startY}%`,
                    width: 6, height: 6, borderRadius: "50%", background: color,
                    animation: `confettiBurst 0.8s ease-out ${delay}s forwards`,
                  }}/>
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "baseline", gap: 2, position: "relative" }}>
              <span style={{
                fontSize: 52, fontWeight: 800, lineHeight: 1, letterSpacing: -2,
                color: scoreColor,
                transition: "color 0.3s ease",
              }}>
                {score}
              </span>
              <span style={{ fontSize: 18, fontWeight: 500, color: "#94a3b8" }}>/100</span>
            </div>
            <div style={{
              fontSize: 12, marginTop: 6, fontWeight: optimizedLabel ? 700 : 400,
              color: optimizedLabel ? "#10B981" : "#94a3b8",
              transition: "color 0.3s ease",
            }}>
              {optimizedLabel ? "✦ Optimized!" : "Feed Quality Score"}
            </div>
          </div>
        </div>
      </div>

      {/* CTA card */}
      <div style={{
        background: "#fff", borderRadius: 8, border: "1px solid #e1e3e5",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        marginTop: 12, padding: "20px 24px", textAlign: "center",
        opacity: showCTA ? 1 : 0, maxHeight: showCTA ? 200 : 0,
        overflow: "hidden",
        transition: "opacity 0.4s ease, max-height 0.4s ease",
      }}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>✅</div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 6, lineHeight: 1.3 }}>
          This is what MagicFeedPro does for your entire catalog
        </h3>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
          AI-optimized titles, descriptions & highlights — automatically.
        </p>
        <button style={{
          width: "100%", padding: "12px", background: "linear-gradient(135deg,#4B6AE8,#6B8BF5)",
          border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 600,
          fontFamily: "'Inter',sans-serif", cursor: "pointer",
          boxShadow: "0 4px 16px rgba(75,106,232,0.35)",
        }}>
          → Start Your Free Trial
        </button>
        <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
          No contract • Setup in 10 min • 200+ 5-star reviews
        </p>
      </div>
    </div>
  );
}

/* ── Audit flow utilities ── */
function isValidStoreUrl(url) {
  const cleaned = url.replace(/^https?:\/\//, "").replace(/\/.*$/, "").trim();
  return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/.test(cleaned);
}

function cleanStoreUrl(url) {
  return url.replace(/^https?:\/\//, "").replace(/\/.*$/, "").trim();
}

// Filter out gift cards, placeholder products, and low-quality entries
function filterAndRankProducts(products) {
  const dominated = /gift\s*card|carte\s*cadeau|bon\s*cadeau|e-?gift|chèque/i;
  return products
    .filter(p => {
      const title = (p.title || "").toLowerCase();
      const type = (p.product_type || "").toLowerCase();
      // Exclude gift cards
      if (dominated.test(title) || dominated.test(type) || type === "gift_card") return false;
      // Exclude very short/empty titles (placeholders)
      if ((p.title || "").trim().length < 5) return false;
      return true;
    })
    .sort((a, b) => {
      // Score: prefer products with longer descriptions, more variants, more images
      const scoreA = (a.body_html || "").length + (a.variants?.length || 0) * 50 + (a.images?.length || 0) * 30;
      const scoreB = (b.body_html || "").length + (b.variants?.length || 0) * 50 + (b.images?.length || 0) * 30;
      return scoreB - scoreA;
    });
}

async function fetchStoreProducts(storeName) {
  let data;

  // Attempt 1: Best-selling products via /collections/all (most relevant for audit)
  try {
    const res = await fetch(`/api/shopify-proxy?store=${encodeURIComponent(storeName)}&endpoint=collections/all/products.json&limit=50&sort_by=best-selling`);
    if (res.ok) {
      const json = await res.json();
      if (json.products && json.products.length > 0) data = json;
    }
  } catch {}

  // Attempt 2: Regular products endpoint via our proxy
  if (!data) {
    try {
      const res = await fetch(`/api/shopify-proxy?store=${encodeURIComponent(storeName)}&endpoint=products.json&limit=50`);
      if (res.ok) data = await res.json();
    } catch {}
  }

  // Attempt 3: allorigins proxy (fallback for local dev)
  if (!data) {
    try {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(`https://${storeName}/collections/all/products.json?sort_by=best-selling&limit=50`)}`;
      const res = await fetch(proxyUrl);
      if (res.ok) {
        const wrapper = await res.json();
        data = JSON.parse(wrapper.contents);
      }
    } catch {}
  }

  // Attempt 4: allorigins basic fallback
  if (!data) {
    try {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(`https://${storeName}/products.json?limit=50`)}`;
      const res = await fetch(proxyUrl);
      if (res.ok) {
        const wrapper = await res.json();
        data = JSON.parse(wrapper.contents);
      }
    } catch {}
  }

  // Attempt 5: corsproxy.io (last resort)
  if (!data) {
    try {
      const res = await fetch(`https://corsproxy.io/?${encodeURIComponent(`https://${storeName}/products.json?limit=50`)}`);
      if (res.ok) data = await res.json();
    } catch {}
  }

  if (!data) throw new Error("fetch_failed");
  if (!data.products || data.products.length === 0) throw new Error("no_products");

  // Filter out gift cards & rank by quality, then format for Claude
  const ranked = filterAndRankProducts(data.products);
  if (ranked.length === 0) throw new Error("no_products");

  return ranked.map(p => ({
    title: p.title || "",
    description: p.body_html ? p.body_html.replace(/<[^>]*>/g, "").slice(0, 500) : "",
    tags: p.tags || "",
    product_type: p.product_type || "",
    vendor: p.vendor || "",
  }));
}

async function fetchTotalProductCount(storeName) {
  try {
    // Try our proxy first
    let data;
    try {
      const res = await fetch(`/api/shopify-proxy?store=${encodeURIComponent(storeName)}&endpoint=products/count.json`);
      if (res.ok) data = await res.json();
    } catch {}

    if (!data) {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(`https://${storeName}/products/count.json`)}`;
      const res = await fetch(proxyUrl);
      if (res.ok) {
        const wrapper = await res.json();
        data = JSON.parse(wrapper.contents);
      }
    }

    if (!data) {
      const res = await fetch(`https://corsproxy.io/?${encodeURIComponent(`https://${storeName}/products/count.json`)}`);
      if (res.ok) data = await res.json();
    }

    return data?.count || null;
  } catch { return null; }
}

async function analyzeWithClaude(products) {
  let apiKey = "";
  try { apiKey = JSON.parse(localStorage.getItem("magicfeed_tracking") || "{}").anthropicApiKey || ""; } catch {}
  const res = await fetch("/api/analyze", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ products, apiKey }),
  });
  if (!res.ok) throw new Error("api_failed");
  const data = await res.json();
  if (!data.content || !data.content[0] || !data.content[0].text) throw new Error("api_failed");
  // Strip markdown backticks if present (Haiku sometimes wraps in ```json ... ```)
  let rawText = data.content[0].text.trim();
  if (rawText.startsWith("```")) {
    rawText = rawText.replace(/^```(?:json)?\s*/, "").replace(/\s*```$/, "");
  }
  return JSON.parse(rawText);
}

function MagicFeedLogo({ centered = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: centered ? "center" : "flex-start" }}>
      <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
        <defs>
          <linearGradient id="mfgL" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#6B8BF5"/>
            <stop offset="100%" stopColor="#4B6AE8"/>
          </linearGradient>
        </defs>
        <path d="M18 4L20.5 14L31 16.5L20.5 19L18 29L15.5 19L5 16.5L15.5 14Z" fill="url(#mfgL)"/>
        <path d="M34 6L35.2 10L39 11.2L35.2 12.4L34 16L32.8 12.4L29 11.2L32.8 10Z" fill="url(#mfgL)" opacity="0.7"/>
        <circle cx="40" cy="22" r="2" fill="url(#mfgL)" opacity="0.5"/>
      </svg>
      <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 22, lineHeight: 1, letterSpacing: -0.3 }}>
        <span style={{ fontWeight: 800, color: "#0f172a" }}>Magic</span>
        <span style={{ fontWeight: 400, color: "#0f172a" }}>Feed</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#4B6AE8", marginLeft: 2 }}>Pro</span>
      </span>
    </div>
  );
}

export { ShoppingWidget, AIFixCard, MagicFeedLogo, ScanProductCard, ProductCard, SCAN_PRODUCTS };

export default function MagicFeedProSignup() {
  const navigate = useNavigate();
  const [storeUrl, setStoreUrl] = useState("");
  const [flowStep, setFlowStep] = useState("idle"); // idle → scanning → results

  useEffect(() => {
    // Capture UTM params on landing (before URL changes)
    try {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach(k => {
        const v = params.get(k);
        if (v) utms[k] = v;
      });
      if (Object.keys(utms).length > 0) sessionStorage.setItem("mfp_utms", JSON.stringify(utms));
    } catch {}
    // Load tracking config from server (non-blocking, falls back to localStorage)
    fetch("/api/data?key=tracking")
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (json && json.data) {
          let cfg = json.data;
          if (typeof cfg === "string") { try { cfg = JSON.parse(cfg); } catch { cfg = {}; } }
          if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
            const cleaned = {};
            for (const [k, v] of Object.entries(cfg)) { if (!/^\d+$/.test(k)) cleaned[k] = v; }
            cfg = cleaned;
          }
          if (Object.keys(cfg).length > 0) {
            localStorage.setItem("magicfeed_tracking", JSON.stringify(cfg));
            refreshConfig();
          }
        }
      })
      .catch(() => {})
      .finally(() => { initTracking(); trackPageView("home"); });
  }, []);
  const [scanPct, setScanPct] = useState(0);
  const [scanLabel, setScanLabel] = useState("");
  const [scanSteps, setScanSteps] = useState([false, false, false]); // titles, desc, highlights done
  const [auditData, setAuditData] = useState(null);
  const [displayScore, setDisplayScore] = useState(0);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [productCount, setProductCount] = useState(0);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = cleanStoreUrl(storeUrl.trim());
    if (!cleaned || !isValidStoreUrl(cleaned)) {
      setError("Please enter a valid URL (ex: yourstore.com)");
      return;
    }

    setFlowStep("scanning");
    setScanPct(0);
    setScanSteps([false, false, false]);

    try {
      // Step 1: Connecting — fetch real products
      setScanLabel("Connecting to store...");
      for (let i = 0; i <= 15; i++) { setScanPct(i); await sleep(30); }

      let products;
      try {
        products = await fetchStoreProducts(cleaned);
      } catch (err) {
        const msg = err.message === "no_products"
          ? "No products found. Make sure your store is public."
          : "We couldn't reach this store. Check the URL and try again.";
        setError(msg);
        setFlowStep("idle");
        return;
      }

      // Only track URL sent after successful store connection
      try { trackUrlSent(cleaned); } catch {}

      // Fetch total product count in parallel (non-blocking)
      const totalCountPromise = fetchTotalProductCount(cleaned);

      for (let i = 16; i <= 30; i++) { setScanPct(i); await sleep(30); }
      setProductCount(products.length);

      // Step 2: Analyzing titles
      setScanLabel("Analyzing titles...");
      for (let i = 31; i <= 50; i++) { setScanPct(i); await sleep(30); }
      setScanSteps([true, false, false]);

      // Step 3: Checking descriptions — start Claude analysis
      setScanLabel("Checking descriptions...");
      let claudeResult;
      const claudePromise = analyzeWithClaude(products).then(r => { claudeResult = r; });

      for (let i = 51; i <= 70; i++) { setScanPct(i); await sleep(40); }
      setScanSteps([true, true, false]);

      // Step 4: Evaluating highlights — wait for Claude to finish
      setScanLabel("Evaluating highlights...");
      for (let i = 71; i <= 85; i++) { setScanPct(i); await sleep(40); }

      // Wait for Claude if not done yet
      await claudePromise;

      setScanSteps([true, true, true]);
      for (let i = 86; i <= 100; i++) { setScanPct(i); await sleep(20); }
      setScanLabel("Analysis complete!");
      await sleep(600);

      if (!claudeResult || typeof claudeResult.globalScore !== "number") {
        throw new Error("parse_failed");
      }

      // Get total count
      const totalCount = await totalCountPromise;
      const totalProducts = totalCount || products.length;

      // Transform Claude response into AuditPage shape
      const ba = (claudeResult.beforeAfter || []).slice(0, 5);

      const auditResult = {
        globalScore: claudeResult.globalScore,
        titleScore: claudeResult.titleScore,
        descriptionScore: claudeResult.descriptionScore,
        highlightsScore: claudeResult.highlightsScore,
        totalProducts,
        analyzedProducts: products.length,
        storeName: cleaned,
        avgTitleLen: claudeResult.avgTitleLength || 0,
        avgDescLen: claudeResult.avgDescriptionLength || 0,
        hlMissingPct: products.length > 0
          ? Math.round(((claudeResult.productsWithNoHighlights || 0) / products.length) * 100)
          : 0,
        criticalIssues: (claudeResult.criticalIssues || []).slice(0, 3),
        // Titles tab: before/after
        beforeAfter: ba.map(p => ({
          before: p.beforeTitle || p.productName || "",
          after: p.afterTitle || "",
          titleScore: p.titleScore,
        })),
        // Descriptions tab
        descriptions: ba.map(p => ({
          before: p.beforeDescription || "",
          after: p.afterDescription || "",
        })),
        // Highlights tab
        highlights: ba.map(p => {
          const beforeHl = (!p.beforeHighlights || p.beforeHighlights === "none" || p.beforeHighlights === "")
            ? null
            : p.beforeHighlights.split(" | ").filter(Boolean);
          const afterHl = (p.afterHighlights || "").split(" | ").filter(Boolean);
          return {
            before: beforeHl,
            after: afterHl.length > 0 ? afterHl : ["No highlights generated"],
          };
        }),
      };

      setAuditData(auditResult);
      setFlowStep("results");

      // Animate score count-up
      const target = auditResult.globalScore;
      const start = Date.now();
      const tick = () => {
        const p = Math.min((Date.now() - start) / 1000, 1);
        setDisplayScore(Math.round(p * target));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);

    } catch (err) {
      const msg = err.message === "parse_failed"
        ? "Analysis failed. Please try again."
        : "Connection error. Please check your internet and try again.";
      setError(msg);
      setFlowStep("idle");
    }
  };

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try { sessionStorage.setItem("mfp_audit", JSON.stringify(auditData)); } catch {}
    // Store lead in localStorage + server for admin panel
    try {
      const leads = JSON.parse(localStorage.getItem("magicfeed_leads") || "[]");
      const utms = (() => { try { return JSON.parse(sessionStorage.getItem("mfp_utms") || "{}"); } catch { return {}; } })();
      const newLead = {
        email: email.trim(),
        storeUrl: cleanStoreUrl(storeUrl.trim()),
        globalScore: auditData.globalScore,
        titleScore: auditData.titleScore,
        descriptionScore: auditData.descriptionScore,
        highlightsScore: auditData.highlightsScore,
        totalProducts: auditData.totalProducts || 0,
        timestamp: new Date().toISOString(),
        auditData: auditData,
        utm_source: utms.utm_source || "",
        utm_medium: utms.utm_medium || "",
        utm_campaign: utms.utm_campaign || "",
        utm_content: utms.utm_content || "",
        utm_term: utms.utm_term || "",
      };
      leads.push(newLead);
      localStorage.setItem("magicfeed_leads", JSON.stringify(leads));
      // Telegram notification (non-blocking)
      try {
        const tCfg = JSON.parse(localStorage.getItem("magicfeed_tracking") || "{}");
        if (tCfg.telegramBotToken && tCfg.telegramChatId) {
          fetch("/api/telegram", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ botToken: tCfg.telegramBotToken, chatId: tCfg.telegramChatId, lead: newLead }),
          }).catch(() => {});
        }
      } catch {}
      // Sync to server (non-blocking)
      fetch("/api/data?key=leads").then(r => r.ok ? r.json() : null).then(json => {
        const serverLeads = (json && Array.isArray(json.data)) ? json.data : [];
        serverLeads.push(newLead);
        fetch("/api/data", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: "leads", value: serverLeads }),
        }).catch(() => {});
      }).catch(() => {});
    } catch {}
    try { trackEmailSent(email.trim(), cleanStoreUrl(storeUrl.trim()), auditData.globalScore); } catch {}
    // Store audit in KV + Klaviyo sync (silent, non-blocking)
    // Helper: run Klaviyo sync with given config
    const runKlaviyoSync = (trackingCfg) => {
      fetch("/api/audit-store", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auditData }),
      })
        .then(r => r.ok ? r.json() : null)
        .then(result => {
          const auditLink = result ? result.url : "";
          if (trackingCfg.klaviyoPrivateKey && trackingCfg.klaviyoListId) {
            fetch("/api/klaviyo", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: email.trim(),
                storeUrl: cleanStoreUrl(storeUrl.trim()),
                globalScore: auditData.globalScore,
                titleScore: auditData.titleScore,
                descriptionScore: auditData.descriptionScore,
                highlightsScore: auditData.highlightsScore,
                totalProducts: auditData.totalProducts || 0,
                auditLink,
                klaviyoPrivateKey: trackingCfg.klaviyoPrivateKey,
                klaviyoListId: trackingCfg.klaviyoListId,
              }),
            }).catch(() => {});
          }
        })
        .catch(() => {});
    };
    try {
      let trackingCfg = JSON.parse(localStorage.getItem("magicfeed_tracking") || "{}");
      if (trackingCfg.klaviyoPrivateKey) {
        // Config already in localStorage — use it directly
        runKlaviyoSync(trackingCfg);
      } else {
        // Fallback: fetch config from server (covers new visitors where initial fetch may have failed)
        fetch("/api/data?key=tracking")
          .then(r => r.ok ? r.json() : null)
          .then(json => {
            if (json && json.data) {
              let cfg = json.data;
              if (typeof cfg === "string") { try { cfg = JSON.parse(cfg); } catch { cfg = {}; } }
              if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
                const cleaned = {};
                for (const [k, v] of Object.entries(cfg)) { if (!/^\d+$/.test(k)) cleaned[k] = v; }
                cfg = cleaned;
              }
              if (Object.keys(cfg).length > 0) {
                localStorage.setItem("magicfeed_tracking", JSON.stringify(cfg));
                refreshConfig();
                runKlaviyoSync(cfg);
              }
            }
          })
          .catch(() => {});
      }
    } catch {}
    setShowConfirmPopup(true);
    setTimeout(() => {
      navigate("/final");
    }, 3000);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { min-height:100%; }
        .mf-input { width:100%; background:#fff; border:1.5px solid #C7D2FE; border-radius:10px; padding:10px 13px; color:#0f172a; font-size:13px; font-family:'Inter',sans-serif; outline:none; transition:border-color 0.2s, box-shadow 0.2s; }
        .mf-input:focus { border-color:#4361EE; box-shadow:0 0 0 3px rgba(67,97,238,0.15); }
        .mf-input::placeholder { color:#9CA3AF; }
        .btn-primary { width:100%; padding:11px; background:linear-gradient(135deg,#4B6AE8,#6B8BF5); border:none; border-radius:10px; color:#fff; font-size:14px; font-weight:600; font-family:'Inter',sans-serif; cursor:pointer; box-shadow:0 4px 16px rgba(75,106,232,0.35); transition:all 0.2s ease; }
        .btn-primary:hover { transform:translateY(-1px); box-shadow:0 6px 22px rgba(75,106,232,0.45); }
        @keyframes floatMF { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes pulseMF { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes shimmerMF { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes popIn { 0%{opacity:0;transform:scale(0.75)} 100%{opacity:1;transform:scale(1)} }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes confettiBurst { 0%{opacity:1;transform:translateY(0) scale(1)} 100%{opacity:0;transform:translateY(-60px) scale(0.5)} }

        .mf-factor-pill:hover { border-color:#4361EE !important; background:#EEF2FF !important; }
        .mf-hide-mobile { display:block; }

        /* Tablet */
        @media (max-width: 768px) {
          .mf-main-card { padding:28px 24px !important; }
          .mf-headline { font-size:28px !important; }
          .mf-input-row { flex-direction:column !important; }
          .mf-input-row .btn-primary { width:100% !important; }
          .mf-summary-inner { flex-direction:column !important; gap:16px !important; }
          .mf-summary-divider { width:100% !important; height:1px !important; }
          .mf-summary-left { padding-right:0 !important; }
          .mf-summary-right { padding-left:0 !important; }
          .mf-score-big { font-size:40px !important; }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .mf-hide-mobile { display:none !important; }
          .mf-main-card { padding:20px 16px !important; border-radius:20px !important; }
          .mf-outer-card { border-radius:20px !important; }
          .mf-headline { font-size:22px !important; letter-spacing:-0.8px !important; }
          .mf-subhead { font-size:13px !important; }
          .mf-subhead br { display:none; }
          .mf-product-cards { gap:8px !important; overflow-x:auto !important; flex-wrap:nowrap !important; -webkit-overflow-scrolling:touch; padding-bottom:4px !important; }
          .mf-product-cards > div { min-width:140px !important; flex:0 0 140px !important; }
          .mf-audit-card { padding:12px !important; }
          .mf-score-big { font-size:36px !important; }
          .mf-score-label { font-size:10px !important; }
          .mf-factor-pill { padding:5px 12px !important; font-size:12px !important; }
          .mf-section-title { font-size:17px !important; }
          .mf-bottom-cta h3 { font-size:18px !important; }
          .mf-global-score-card { flex-direction:column !important; text-align:center !important; gap:10px !important; padding:16px !important; }
          .mf-global-score-card > div:last-child { text-align:center !important; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif", background: "linear-gradient(135deg,#f0f4ff 0%,#e8eeff 50%,#dde5ff 100%)", padding: "20px", position: "relative", overflow: "hidden" }}>

        {/* Background decorative blurs */}
        <div style={{ position:"absolute", width:600, height:600, borderRadius:"50%", background:"radial-gradient(circle,rgba(75,106,232,0.08) 0%,transparent 70%)", top:"-10%", left:"-5%", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(139,92,246,0.06) 0%,transparent 70%)", bottom:"-15%", right:"-5%", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(75,106,232,0.05) 0%,transparent 70%)", top:"50%", left:"50%", transform:"translate(-50%,-50%)", pointerEvents:"none" }}/>

        {/* Floating sparkles on background */}
        {[[8,12,14,0],[85,8,10,1],[92,75,13,2],[5,80,11,1.5]].map(([x,y,s,d],i) => (
          <svg key={i} width={s} height={s} viewBox="0 0 16 16" fill="none" style={{ position:"absolute", left:`${x}%`, top:`${y}%`, animation:`floatMF 4s ease-in-out ${d}s infinite`, zIndex:0 }}>
            <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="#a5b4fc" opacity="0.45"/>
          </svg>
        ))}

        {/* Single centered column */}
        <div style={{ maxWidth: 672, width: "100%", zIndex: 1 }}>

          {/* ── MAIN CARD ── */}
          <div className="mf-outer-card" style={{ background: "linear-gradient(180deg, #f8faff 0%, #f0f4ff 100%)", borderRadius: 28, overflow: "hidden", boxShadow: "0 12px 60px rgba(75,106,232,0.12), 0 2px 8px rgba(0,0,0,0.04)", position: "relative" }}>

            <div className="mf-main-card" style={{ padding: "32px 36px", position: "relative" }}>

              {/* Floating sparkles inside card */}
              {[[62,8,14,0],[70,14,10,1],[15,72,13,2]].map(([x,y,s,d],i) => (
                <svg key={i} width={s} height={s} viewBox="0 0 16 16" fill="none" style={{ position:"absolute", left:`${x}%`, top:`${y}%`, animation:`floatMF 4s ease-in-out ${d}s infinite` }}>
                  <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="#a5b4fc" opacity="0.35"/>
                </svg>
              ))}

              {/* ═══ IDLE STATE ═══ */}
              {flowStep === "idle" && (
                <>
                  <h1 id="hero" className="mf-headline" style={{ fontSize:32, fontWeight:800, color:"#0f172a", lineHeight:1.2, letterSpacing:-1.2, marginBottom:10, textAlign:"center" }}>
                    What's your <span style={{ background:"linear-gradient(135deg,#4B6AE8,#818cf8)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Google Shopping</span><br/>feed score?
                  </h1>
                  <p className="mf-subhead" style={{ fontSize:14, color:"#6b7280", lineHeight:1.7, marginBottom:20, textAlign:"center" }}>
                    Enter your Shopify store URL and get a free AI audit of your product feed<br/><strong style={{ color:"#0f172a", fontWeight:600 }}>and see how much visibility you're losing on Google Shopping.</strong>
                  </p>
                  <p style={{ fontSize:13, color:"#EF4444", lineHeight:1.6, marginBottom:20, textAlign:"center", fontWeight:500 }}>
                    ⚠️ Most e-commerce stores lose 30–50% of their Google Shopping traffic<br/>because their product feed isn't optimized.
                  </p>

                  <form id="audit-form" onSubmit={handleAnalyze}>
                    <div className="mf-input-row" style={{ display:"flex", gap:10, marginBottom:8 }}>
                      <input className="mf-input" type="text" placeholder="Example: nike-store.com (Shopify stores only)" value={storeUrl} onChange={e => { setStoreUrl(e.target.value); setError(""); }} style={{ flex:1 }}/>
                      <button className="btn-primary" type="submit" style={{ width:"auto", padding:"11px 24px", whiteSpace:"nowrap" }}>→ Get My Feed Score</button>
                    </div>
                    {error && <p style={{ fontSize:12, color:"#EF4444", textAlign:"center", marginBottom:8, fontWeight:500 }}>{error}</p>}
                    <p style={{ fontSize:12, color:"#94a3b8", textAlign:"center", marginBottom:0 }}>Free • 30 seconds • No account needed</p>
                  </form>

                  {/* ── #scoring-factors — Analyze Title Quality ── */}
                  <div id="scoring-factors" style={{ marginTop: 20, marginBottom: 24 }}>
                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                      <div className="mf-section-title" style={{ fontSize: 20, fontWeight: 600, color: "#0D0D1A", marginBottom: 12 }}>We score every product on 3 factors:</div>
                      <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                        {[{ icon: "📝", label: "Title" }, { icon: "📄", label: "Description" }, { icon: "⭐", label: "Product Highlights" }].map(p => (
                          <span key={p.label} className="mf-factor-pill" style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 600, color: "#4361EE", boxShadow: "0 2px 8px rgba(67,97,238,0.08)", display: "inline-flex", alignItems: "center", gap: 6, cursor: "default", transition: "border-color 0.15s ease, background 0.15s ease" }}>
                            <span style={{ fontSize: 14 }}>{p.icon}</span>{p.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Title quality detail */}
                    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: "18px 20px", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 18 }}>📝</span>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Title Quality Analysis</div>
                      </div>
                      <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, marginBottom: 14 }}>
                        Google Shopping heavily relies on product titles to rank your listings. Short, generic titles like <em>"Laptop bag"</em> get buried — while keyword-rich titles with brand, material, and attributes rank higher and get more clicks.
                      </p>
                      <div style={{ display: "flex", gap: 10 }}>
                        <div style={{ flex: 1, background: "#FEF2F2", borderRadius: 8, padding: "10px 12px", border: "1px solid #FECACA" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: "#EF4444", marginBottom: 4 }}>✗ BEFORE</div>
                          <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 600 }}>Laptop bag</div>
                          <div style={{ fontSize: 10, color: "#EF4444", marginTop: 4 }}>Score: 18/100</div>
                        </div>
                        <div style={{ flex: 1, background: "#F0FDF4", borderRadius: 8, padding: "10px 12px", border: "1px solid #BBF7D0" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: "#10B981", marginBottom: 4 }}>✓ AI-OPTIMIZED</div>
                          <div style={{ fontSize: 11, color: "#065f46", fontWeight: 600, lineHeight: 1.35 }}>Premium Luxury Laptop Backpack 15.6" — Waterproof</div>
                          <div style={{ fontSize: 10, color: "#10B981", marginTop: 4 }}>Score: 94/100</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── #description-analysis — Description & SEO Check ── */}
                  <div id="description-analysis" style={{ marginBottom: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: "18px 20px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 18 }}>📄</span>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Description & SEO Check</div>
                      </div>
                      <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, marginBottom: 14 }}>
                        Google recommends descriptions of 500–5,000 characters with relevant keywords. Most stores have descriptions under 150 characters — that's a massive SEO opportunity being wasted.
                      </p>
                      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 10, color: "#EF4444", fontWeight: 600 }}>Typical store: 120 chars</span>
                            <span style={{ fontSize: 10, color: "#10B981", fontWeight: 600 }}>After AI: 4,970 chars</span>
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: "#F3F4F6", overflow: "hidden", position: "relative" }}>
                            <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: "2.4%", borderRadius: 999, background: "#EF4444" }}/>
                            <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: "99%", borderRadius: 999, background: "linear-gradient(90deg, #EF4444 2.4%, #F59E0B 30%, #10B981 60%)", opacity: 0.85 }}/>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                            <span style={{ fontSize: 9, color: "#94a3b8" }}>0</span>
                            <span style={{ fontSize: 9, color: "#94a3b8" }}>5,000 chars (Google recommended)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── #highlights-check — Audit Product Highlights ── */}
                  <div id="highlights-check" style={{ marginBottom: 20 }}>
                    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: "18px 20px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 18 }}>⭐</span>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Product Highlights Audit</div>
                      </div>
                      <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, marginBottom: 14 }}>
                        Product Highlights appear directly in Google Shopping results and help shoppers make buying decisions. Yet <strong style={{ color: "#EF4444" }}>87% of Shopify stores</strong> have zero highlights configured — a free visibility boost left on the table.
                      </p>
                      <div style={{ display: "flex", gap: 10 }}>
                        <div style={{ flex: 1, background: "#FEF2F2", borderRadius: 8, padding: "10px 12px", border: "1px solid #FECACA" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: "#EF4444", marginBottom: 6 }}>✗ MOST STORES</div>
                          {["Material", "Fit & Sizing", "Care", "Season", "Certification"].map((h, i) => (
                            <div key={i} style={{ fontSize: 11, color: "#d1d5db", padding: "2px 0", display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ fontSize: 10, color: "#d1d5db" }}>○</span>{h}
                            </div>
                          ))}
                          <div style={{ fontSize: 10, color: "#EF4444", fontWeight: 600, marginTop: 6 }}>0/5 highlights</div>
                        </div>
                        <div style={{ flex: 1, background: "#F0FDF4", borderRadius: 8, padding: "10px 12px", border: "1px solid #BBF7D0" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: "#10B981", marginBottom: 6 }}>✓ WITH MAGICFEEDPRO</div>
                          {["100% organic cotton", "Regular fit, true to size", "Machine wash cold", "All-season wear", "OEKO-TEX certified"].map((h, i) => (
                            <div key={i} style={{ fontSize: 11, color: "#065f46", padding: "2px 0", display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ fontSize: 10, color: "#10B981", fontWeight: 700 }}>✓</span>{h}
                            </div>
                          ))}
                          <div style={{ fontSize: 10, color: "#10B981", fontWeight: 600, marginTop: 6 }}>5/5 highlights</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── #example-scores — View Sample Reports ── */}
                  <div id="example-scores">
                    <AuditPreviewCard/>
                  </div>

                  {/* Bottom CTA */}
                  <div className="mf-bottom-cta" style={{ maxWidth: 520, margin: "48px auto 0", textAlign: "center", background: "linear-gradient(135deg, #e0e7ff 0%, #d4dbf9 50%, #ddd6fe 100%)", borderRadius: 16, border: "1px solid rgba(75,106,232,0.18)", boxShadow: "0 4px 24px rgba(75,106,232,0.12)", padding: "36px 32px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: -50, right: -50, width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle, rgba(75,106,232,0.06) 0%, transparent 70%)", pointerEvents: "none" }}/>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0D0D1A", marginBottom: 8 }}>Ready to see your real feed score?</h3>
                    <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 24 }}>Enter your store URL and get your free audit in 30 seconds.</p>
                    <form onSubmit={handleAnalyze}>
                      <input className="mf-input" type="text" placeholder="Example: nike-store.com (Shopify stores only)" value={storeUrl} onChange={e => setStoreUrl(e.target.value)} style={{ marginBottom: 12 }}/>
                      <button className="btn-primary" type="submit" style={{ width: "100%" }}>→ Get My Feed Score</button>
                    </form>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 16, alignItems: "center" }}>
                      {["No signup required", "30-second audit"].map((text, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ color: "#10B981", fontSize: 13, fontWeight: 700 }}>✓</span>
                          <span style={{ fontSize: 12, color: "#6B7280" }}>{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ═══ SCANNING STATE ═══ */}
              {flowStep === "scanning" && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 16 }}>🔍</div>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0D0D1A", marginBottom: 6 }}>
                    Scanning your feed...
                  </h2>
                  <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 28 }}>
                    Analyzing <span style={{ fontWeight: 700, color: "#0D0D1A" }}>{cleanStoreUrl(storeUrl)}</span>
                  </p>

                  {/* Progress bar */}
                  <div style={{ maxWidth: 360, margin: "0 auto 20px", height: 8, background: "#E5E7EB", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 999, background: "linear-gradient(90deg, #4B6AE8, #818cf8)", width: `${scanPct}%`, transition: "width 0.15s linear" }}/>
                  </div>

                  <p style={{ fontSize: 13, fontWeight: 600, color: "#4361EE", marginBottom: 24 }}>{scanLabel}</p>

                  {/* Scan steps checklist */}
                  <div style={{ display: "inline-flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
                    {["Analyzing product titles...", "Checking descriptions quality...", "Evaluating product highlights..."].map((label, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, opacity: scanPct > i * 25 ? 1 : 0.3, transition: "opacity 0.3s ease" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: scanSteps[i] ? "#10B981" : scanPct > i * 25 ? "#4361EE" : "#D1D5DB", transition: "color 0.3s ease" }}>
                          {scanSteps[i] ? "✓" : scanPct > i * 25 ? "⟳" : "○"}
                        </span>
                        <span style={{ fontSize: 13, color: scanPct > i * 25 ? "#374151" : "#D1D5DB" }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ═══ RESULTS STATE ═══ */}
              {flowStep === "results" && auditData && (
                <div style={{ animation: "popIn 0.4s ease" }}>
                  {/* Header */}
                  <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0D0D1A", lineHeight: 1.3, marginBottom: 4 }}>
                      Audit complete for <span style={{ color: "#4361EE" }}>{cleanStoreUrl(storeUrl)}</span>
                    </h2>
                    <p style={{ fontSize: 13, color: "#6B7280" }}>{productCount} products scanned • {auditData.criticalIssues.length} issues found</p>
                  </div>

                  {/* Global score card — VISIBLE */}
                  {(() => {
                    const color = getScoreColor(displayScore);
                    const bgColor = displayScore < 40 ? "#FEF2F2" : displayScore < 70 ? "#FFFBEB" : "#F0FDF4";
                    const borderColor = displayScore < 40 ? "#FECACA" : displayScore < 70 ? "#FDE68A" : "#BBF7D0";
                    const headline = displayScore < 40 ? "Critical issues detected on your feed."
                      : displayScore < 70 ? "Your feed is underperforming."
                      : "Good feed — but you're leaving money on the table.";
                    return (
                      <div className="mf-global-score-card" style={{ display: "flex", alignItems: "center", gap: 16, background: bgColor, borderRadius: 14, padding: "18px 22px", border: `1px solid ${borderColor}`, marginBottom: 20 }}>
                        <div style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
                          <svg width="72" height="72" viewBox="0 0 72 72" style={{ transform: "rotate(-90deg)" }}>
                            <circle cx="36" cy="36" r="30" fill="none" stroke={borderColor} strokeWidth="5"/>
                            <circle cx="36" cy="36" r="30" fill="none" stroke={color} strokeWidth="5"
                              strokeDasharray={`${(displayScore / 100) * 188.5} 188.5`}
                              strokeLinecap="round" style={{ transition: "stroke-dasharray 0.1s linear" }}/>
                          </svg>
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                            <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{displayScore}</span>
                            <span style={{ fontSize: 8, color: "#94a3b8" }}>/100</span>
                          </div>
                        </div>
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: "#0D0D1A", marginBottom: 2 }}>Feed Quality Score</div>
                          <div style={{ fontSize: 13, color, fontWeight: 600 }}>{headline}</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Sub-scores — BLURRED */}
                  <div style={{ position: "relative", marginBottom: 16 }}>
                    <div style={{ filter: "blur(5px)", userSelect: "none", pointerEvents: "none" }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        {[
                          { icon: "📝", label: "Title Score", score: auditData.titleScore },
                          { icon: "📄", label: "Description", score: auditData.descriptionScore },
                          { icon: "⭐", label: "Highlights", score: auditData.highlightsScore },
                        ].map((s, i) => (
                          <div key={i} style={{ flex: 1, background: "#F9FAFB", borderRadius: 10, padding: "14px 10px", border: "1px solid #E5E7EB", textAlign: "center" }}>
                            <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
                            <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 4 }}>{s.label}</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: getScoreColor(s.score) }}>{s.score}/100</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.25)", borderRadius: 10 }}>
                      <span style={{ fontSize: 20 }}>🔒</span>
                    </div>
                  </div>

                  {/* Critical issues — VISIBLE */}
                  <div style={{ marginBottom: 20 }}>
                    {auditData.criticalIssues.map((issue, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", marginBottom: 4, borderRadius: 8, background: "rgba(254,226,226,0.4)" }}>
                        <span style={{ fontSize: 13, color: "#EF4444", flexShrink: 0, fontWeight: 700 }}>⚠</span>
                        <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.4 }}>{issue}</span>
                      </div>
                    ))}
                  </div>

                  {/* Before/After table — BLURRED with overlay */}
                  <div style={{ position: "relative", marginBottom: 28 }}>
                    <div style={{ filter: "blur(5px)", userSelect: "none", pointerEvents: "none" }}>
                      <div style={{ background: "#F9FAFB", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden" }}>
                        <div style={{ display: "flex", background: "#F3F4F6", padding: "10px 14px", gap: 8 }}>
                          <span style={{ flex: 2, fontSize: 11, fontWeight: 700, color: "#6B7280" }}>Current Title</span>
                          <span style={{ flex: 2, fontSize: 11, fontWeight: 700, color: "#4361EE" }}>✦ AI Optimized</span>
                        </div>
                        {auditData.beforeAfter.map((row, i) => (
                          <div key={i} style={{ display: "flex", padding: "10px 14px", gap: 8, borderTop: "1px solid #E5E7EB" }}>
                            <span style={{ flex: 2, fontSize: 11, color: "#9CA3AF", lineHeight: 1.4 }}>{row.before}</span>
                            <span style={{ flex: 2, fontSize: 11, color: "#374151", fontWeight: 600, lineHeight: 1.4 }}>{row.after}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Overlay */}
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.45)", borderRadius: 12 }}>
                      <span style={{ fontSize: 24, marginBottom: 8 }}>🔒</span>
                      <span style={{ fontSize: 18, fontWeight: 700, color: "#0D0D1A", marginBottom: 4 }}>Your full optimization report is ready</span>
                      <span style={{ fontSize: 13, color: "#6B7280" }}>See exactly how to fix your titles, descriptions & highlights.</span>
                    </div>
                  </div>

                  {/* Email gate */}
                  <div style={{ background: "linear-gradient(135deg, #eef2ff, #e0e7ff)", borderRadius: 14, padding: "24px 20px", border: "1px solid rgba(75,106,232,0.12)", textAlign: "center" }}>
                    <form onSubmit={handleUnlock}>
                      <input className="mf-input" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} style={{ marginBottom: 12, fontSize: 14, padding: "12px 14px" }}/>
                      <button className="btn-primary" type="submit" style={{ width: "100%" }}>→ Unlock My Report</button>
                    </form>
                    <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>No spam • Unsubscribe anytime</p>
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>{/* end max-w-2xl */}
      </div>

      {/* Confirmation popup before redirect */}
      {showConfirmPopup && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            border: "1px solid rgba(99,102,241,0.3)",
            borderRadius: 20, padding: "48px 40px", maxWidth: 460, width: "90%",
            textAlign: "center", animation: "popIn .4s ease",
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%", margin: "0 auto 20px",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 32,
            }}>
              ✉️
            </div>
            <h3 style={{
              color: "#fff", fontSize: 22, fontWeight: 700, marginBottom: 12,
              fontFamily: "'Inter', sans-serif",
            }}>
              Your full audit report is on its way!
            </h3>
            <p style={{
              color: "rgba(255,255,255,0.65)", fontSize: 15, lineHeight: 1.6,
              fontFamily: "'Inter', sans-serif", marginBottom: 24,
            }}>
              We're sending the detailed audit to <strong style={{ color: "#a5b4fc" }}>{email}</strong>.
              You'll receive it within a few minutes.
            </p>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              color: "rgba(255,255,255,0.5)", fontSize: 14,
              fontFamily: "'Inter', sans-serif",
            }}>
              <span style={{
                width: 20, height: 20, border: "3px solid rgba(99,102,241,0.3)",
                borderTopColor: "#6366f1", borderRadius: "50%",
                animation: "spin 1s linear infinite", display: "inline-block",
              }} />
              Redirecting you to MagicFeedPro...
            </div>
          </div>
        </div>
      )}
    </>
  );
}
